/**
 * Web Component entry point for the Social widget.
 *
 * Importing this module registers the <legion-social> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/social-web-component';
 *
 * // Then use in HTML:
 * // <legion-social api-url="https://your-domain.com" layout="feed" />
 * ```
 */

import { SOCIAL_THEMING_PROP_TO_PARAM } from "./types";
import { attachAuthBridge, type AuthBridgeHandle, AUTH_BRIDGE_OBSERVED_ATTRS } from "../shared/auth-bridge";

const SOCIAL_CSS_ATTRIBUTES = Object.values(SOCIAL_THEMING_PROP_TO_PARAM);

class LegionSocialElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url",
      
      "width",
      "height",
      "theme",
      "layout",
      "rows",
      "cols",
      "community-url",
      "channel",
      ...SOCIAL_CSS_ATTRIBUTES,
    
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.bridgeBaseUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(
    _name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  private get apiUrl(): string {
    return this.getAttribute("api-url") || window.location.origin;
  }

  private get authToken(): string | null {
    return this.getAttribute("auth-token");
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "600";
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "800";
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get layout(): string {
    return (this.getAttribute("layout") || "feed").toLowerCase();
  }

  private get rows(): number {
    return parseInt(this.getAttribute("rows") || "3", 10);
  }

  private get cols(): number {
    return parseInt(this.getAttribute("cols") || "3", 10);
  }

  private get communityUrl(): string {
    return this.getAttribute("community-url") || "";
  }

  private get channel(): string {
    return this.getAttribute("channel") || "";
  }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/social", this.apiUrl);

    if (this.layout) url.searchParams.set("layout", this.layout);
    if (this.theme) url.searchParams.set("theme", this.theme);

    if (this.layout === "grid") {
      url.searchParams.set("rows", String(this.rows));
      url.searchParams.set("cols", String(this.cols));
      if (this.communityUrl) {
        url.searchParams.set("communityUrl", this.communityUrl);
      }
    } else if (this.layout === "feed") {
      if (this.widgetHeight && this.widgetHeight !== "800") {
        url.searchParams.set("height", this.widgetHeight);
      }
    }

    if (this.channel) {
      url.searchParams.set("channel", this.channel);
    }

    SOCIAL_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = this.widgetHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "auto";
    iframe.title = "Social Feed Widget";

    const requiresAuth = this.layout !== "grid";

    iframe.onload = () => {
      if (requiresAuth && this.authToken && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token: this.authToken },
          this.apiUrl
        );
      }
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: { widget: "social", layout: this.layout, theme: this.theme },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load social widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${this.widgetWidth}px;
        height: ${this.widgetHeight}px;
      }
      iframe {
        width: 100%;
        height: 100%;
        border: none;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        background: ${this.theme === "dark" ? "#1a1a1a" : "white"};
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }

  private get bridgeBaseUrl(): string {
    return this.apiUrl;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-social")
) {
  customElements.define("legion-social", LegionSocialElement);
}

// Backward compatibility: support <social-widget> tag
if (
  typeof customElements !== "undefined" &&
  !customElements.get("social-widget")
) {
  customElements.define("social-widget", LegionSocialElement);
}

export { LegionSocialElement };

